import { useMemo } from 'react';
import { useCustomMutation, useInvalidate, useGo } from '@refinedev/core';
import { notify } from '@/lib/notify';
import { Check, X, Eye, RotateCcw } from 'lucide-react';
import { PaymentStatus } from '@masters-club/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useConfirm } from '@/hooks/use-confirm';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import { UniversalTable } from '../../components/universal';
import type { BulkActionConfig, EntityConfig } from '../../components/universal/types';
import { paymentConfig } from './config';
import { AnalyticsTab } from './components/AnalyticsTab';
import type { Payment } from './types';

export function PaymentsList() {
  useScrollRestoration();
  const { mutateAsync: updatePaymentStatus } = useCustomMutation<{ success: boolean }>();
  const invalidate = useInvalidate();
  const go = useGo();
  const { confirm, ConfirmationDialog } = useConfirm();

  const runBulkStatusUpdate = async (
    rows: Payment[],
    status: PaymentStatus,
    actionLabel: string,
  ) => {
    const results = await Promise.allSettled(
      rows.map((row) =>
        updatePaymentStatus({
          url: `/payments/${row.id}/status`,
          method: 'patch',
          values: { status },
        }),
      ),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - succeeded;

    if (succeeded > 0) {
      notify.success(`${actionLabel}: ${succeeded} из ${results.length}`);
    }
    if (failed > 0) {
      notify.error(`Не удалось обработать: ${failed}`);
    }

    await invalidate({ resource: 'payments', invalidates: ['list'] });
  };

  const handleBulkConfirm = async (rows: Payment[]) => {
    const confirmed = await confirm({
      title: `Подтвердить ${rows.length} платежей?`,
      description: 'Выбранные платежи будут переведены в статус «Подтверждён».',
      confirmText: 'Подтвердить',
      variant: 'default',
    });
    if (!confirmed) { return; }
    await runBulkStatusUpdate(rows, PaymentStatus.CONFIRMED, 'Подтверждено');
  };

  const handleBulkReject = async (rows: Payment[]) => {
    const confirmed = await confirm({
      title: `Отклонить ${rows.length} платежей?`,
      description: 'Выбранные платежи будут переведены в статус «Отклонён». Это действие нельзя отменить.',
      confirmText: 'Отклонить',
      variant: 'destructive',
    });
    if (!confirmed) { return; }
    await runBulkStatusUpdate(rows, PaymentStatus.REJECTED, 'Отклонено');
  };

  const handleBulkRefund = async (rows: Payment[]) => {
    const confirmed = await confirm({
      title: `Возврат ${rows.length === 1 ? 'платежа' : `${rows.length} платежей`}?`,
      description: 'Выбранные платежи будут переведены в статус «Возврат».',
      confirmText: 'Выполнить возврат',
      variant: 'destructive',
    });
    if (!confirmed) { return; }
    await runBulkStatusUpdate(rows, PaymentStatus.REFUNDED, 'Возврат выполнен');
  };

  const bulkActions: BulkActionConfig<Payment>[] = useMemo(
    () => [
      {
        key: 'view',
        label: 'Просмотр',
        icon: <Eye className="h-4 w-4" />,
        variant: 'outline',
        onClick: (rows) => {
          if (rows.length !== 1) {
            notify.info('Выберите один платёж для просмотра');
            return;
          }
          go({ to: `/payments/show/${rows[0].id}` });
        },
      },
      {
        key: 'confirm',
        label: 'Подтвердить',
        icon: <Check className="h-4 w-4" />,
        variant: 'default',
        onClick: handleBulkConfirm,
      },
      {
        key: 'refund',
        label: 'Возврат',
        icon: <RotateCcw className="h-4 w-4" />,
        variant: 'destructive',
        onClick: handleBulkRefund,
      },
      {
        key: 'reject',
        label: 'Отклонить',
        icon: <X className="h-4 w-4" />,
        variant: 'destructive',
        onClick: handleBulkReject,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const configWithBulkActions: EntityConfig<Payment> = { ...paymentConfig, bulkActions };

  return (
    <Tabs defaultValue="list" className="space-y-4">
      {ConfirmationDialog}
      <TabsList>
        <TabsTrigger value="list">Список</TabsTrigger>
        <TabsTrigger value="analytics">Аналитика</TabsTrigger>
      </TabsList>

      <TabsContent value="list" className="mt-0">
        <UniversalTable config={configWithBulkActions} />
      </TabsContent>

      <TabsContent value="analytics" className="mt-0">
        <AnalyticsTab />
      </TabsContent>
    </Tabs>
  );
}
